import styled, { css } from 'styled-components'
import { Input, CheckboxItem, Select, Button, Ratings, InputRange } from '@sainsburys-tech/bolt'
import { themeContract } from './WriteReview.theme.contract'

const RatingWrapper = styled.div`
  display: inline-block;
`

const Rating = styled(Ratings)`
  ${props =>
    props.error &&
    css`
      & svg {
        stroke: ${props.theme.color.semantic.error.base};
      }
    `}

  & :hover svg, & :active svg, & :focus svg {
    stroke: none;
  }
`

const InputField = styled(Input)`
  && {
    margin-bottom: 0;

    & input[type='text'],
    & input[type='email'] {
      ${props =>
        props.isError &&
        css`
          border-width: 2px;
          border-color: ${props.theme.color.semantic.error.base};
          border-radius: 4px;
          margin: 0;
        `}
    }
  }
`

const Form = styled.form`
  > * + * {
    margin-top: ${props => props.theme.size.spacing.lg};
  }
`

const sharedLabelStyles = css`
  display: block;
  margin-bottom: ${props => props.theme.size.spacing.md};
  font-size: ${props => props.theme.font.size.base};
  font-weight: ${props => props.theme.font.weight.bold};
`

const Label = styled.label`
  ${sharedLabelStyles}
`

const LabelSpan = styled.span`
  ${sharedLabelStyles}
`

const OptionalLabel = styled.span`
  color: ${props => props.theme.color.monochrome.darker};
  margin-left: ${props => props.theme.size.spacing.md};
  font-size: ${props => props.theme.font.size.caption};
  display: inline-block;
  text-transform: capitalize;
`

const ReviewTextarea = styled.textarea`
  font-family: ${props => props.theme.font.family.base};
  font-display: swap;
  font-size: ${props => props.theme.font.size.base};
  font-weight: ${props => props.theme.font.weight.regular};
  line-height: ${props => props.theme.font.stack['body-1']['line-height']};
  width: 100%;
  margin-top: ${props => props.theme.size.spacing.md};
  padding: 12px 16px;
  height: 120px;
  border-color: ${props => (props.isError ? props.theme.color.semantic.error.base : 'initial')};
  border-width: ${props => (props.isError ? props.theme.size.border.width.md : props.theme.size.border.width.sm)};
  border-radius: ${props => (props.isError ? '4px' : props.theme.size.border.radius.base)};

  &:hover,
  &:focus,
  &:active {
    border-color: ${props =>
      props.isError
        ? props.theme.color.palette[themeContract[props.theme.channel].button]
        : props.theme.color.palette[themeContract[props.theme.channel].pre.borderColor]};
    border-width: 2px;
    border-radius: 4px;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`

const ErrorMessage = styled.div`
  display: flex;
  font-size: ${props => props.theme.font.size.caption};
  font-weight: ${props => props.theme.font.weight.bold};

  &::before {
    content: '!';
    padding-left: ${props => props.theme.size.spacing.md};
    padding-right: ${props => props.theme.size.spacing.lg};
    color: ${props => props.theme.color.semantic.error.base};
  }
`

const Hint = styled.span`
  display: block;
  font-size: ${props => props.theme.font.size.caption};
  font-weight: ${props => props.theme.font.weight.regular};
`

const GuidelinesToggler = styled.span`
  display: block;
  text-decoration: underline;
  font-size: ${props => props.theme.font.size.caption};
  font-weight: ${props => props.theme.font.weight.regular};
  cursor: help;
`

const SubHeading = styled.h4`
  && {
    margin-bottom: ${props => props.theme.size.spacing.md};
    font-size: ${props => props.theme.font.size.base};
    font-weight: ${props => props.theme.font.weight.bold};
  }
`

const Guidelines = styled.div`
  margin-top: ${props => props.theme.size.spacing.md};
  padding: ${props => props.theme.size.spacing.md};
  background-color: ${props => props.theme.color.monochrome.lighter};

  & ul {
    list-style: inside;
    padding-left: 5px;
  }
`

const GuidelinesParagraph = styled.p`
  && {
    margin: ${props => props.theme.size.spacing.md} 0px 0px 0px;
  }
`

const FormGroup60 = styled.fieldset`
  width: 100%;
  @media only screen and (min-width: ${props => props.theme.size.breakpoint['min-width'].md}) {
    width: 60%;
  }
`

const FormGroup70 = styled.fieldset`
  width: 100%;
  @media only screen and (min-width: ${props => props.theme.size.breakpoint['min-width'].md}) {
    width: 70%;
  }
`

const FormGroupFull = styled.fieldset`
  width: 100%;
`

const Divider = styled.div`
  border-bottom: 1px solid ${props => props.theme.color.monochrome.light};
`

const RecommendItem = styled.span`
  display: inline-block;
  position: relative;
  width: 167px;
  @media only screen and (min-width: ${props => props.theme.size.breakpoint['min-width'].md}) {
    width: 180px;
  }

  ${props =>
    props.first &&
    css`
      margin-right: 5px;
      @media only screen and (min-width: props.theme.size.breakpoint['min-width'].md) {
        margin-right: props.theme.size.spacing.md;
      }
    `}
`

const RecommendInput = styled.input.attrs({ type: 'radio' })`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  margin: 0;
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0.00001;
  width: 100%;
  height: 100%;
  z-index: 2;
  cursor: pointer;

  &:hover ~ label,
  &:focus ~ label,
  &:checked + label {
    border: 2px solid ${props => props.theme.color.monochrome.black};
    color: ${props => props.theme.color.monochrome.black};
    margin: -1px;
  }
`

const RecommendLabel = styled.label`
  cursor: pointer;
  text-transform: capitalize;
  border: ${props => `${props.theme.size.border.width.sm} solid ${props.theme.color.monochrome.base}`};
  padding: 10px 0;
  width: 100%;
  text-align: center;
  display: inline-block;
`

const WordCount = styled.div`
  display: inline-block;
  margin-left: auto;
  font-size: ${props => props.theme.font.size.caption};
  font-weight: ${props => props.theme.font.weight.regular};
  text-align: right;
`

const SelectInput = styled(Select)`
  width: 162px;
  @media only screen and (min-width: ${props => props.theme.size.breakpoint['min-width'].md}) {
    width: 270px;
  }
`

const TacCheckbox = styled(CheckboxItem)`
  && {
    span {
      font-size: 15px;
    }
  }
`

const ImageUploaderInput = styled.input.attrs({ type: 'file', accept: 'image/*' })`
  display: none;
`

const ImageUploaderButton = styled(Button)`
  width: 170px;
  margin-bottom: 5px;
`

const PhotoContainer = styled.div`
  display: flex;
  margin-top: ${props => props.theme.size.spacing.lg};
  max-width: 340px;
  flex-wrap: wrap;
  gap: ${props => props.theme.size.spacing.lg};
`

const ThumbnailContainer = styled.div`
  position: relative;
`

const Thumbnail = styled.img`
  width: 100px;
  height: 100px;
`

const IconWrapper = styled.div.attrs({ tabIndex: 0 })`
  position: absolute;
  top: 0;
  right: 0;
  line-height: 12px;
  cursor: pointer;
`

export {
  RatingWrapper,
  Rating,
  InputField,
  Form,
  FormGroup60,
  FormGroup70,
  FormGroupFull,
  Label,
  OptionalLabel,
  LabelSpan,
  ReviewTextarea,
  ErrorMessage,
  Hint,
  GuidelinesToggler,
  Guidelines,
  GuidelinesParagraph,
  SubHeading,
  Divider,
  RecommendItem,
  RecommendInput,
  RecommendLabel,
  InputRange,
  TacCheckbox,
  WordCount,
  SelectInput,
  Button,
  ImageUploaderInput,
  ImageUploaderButton,
  PhotoContainer,
  ThumbnailContainer,
  Thumbnail,
  IconWrapper
}
